import { toast as sonnerToast } from "sonner";
import React from "react";

/**
 * Branded toast helpers — colored + iconified per intent, with optional undo action.
 * Usage: toast.success("Saved"), toast.error("Failed"), toast.info("Heads up"),
 *        toast.undo("Item deleted", () => restore())
 */
export const toast = {
  success: (msg, opts = {}) =>
    sonnerToast.success(msg, {
      duration: 3500,
      className: "pb-toast-success",
      ...opts,
    }),
  error: (msg, opts = {}) =>
    sonnerToast.error(msg, {
      duration: 5000,
      className: "pb-toast-error",
      ...opts,
    }),
  info: (msg, opts = {}) =>
    sonnerToast(msg, {
      duration: 4000,
      className: "pb-toast-info",
      ...opts,
    }),
  warning: (msg, opts = {}) =>
    sonnerToast.warning(msg, {
      duration: 4500,
      className: "pb-toast-warning",
      ...opts,
    }),
  undo: (msg, onUndo, opts = {}) =>
    sonnerToast(msg, {
      duration: 6000,
      action: { label: "Undo", onClick: onUndo },
      ...opts,
    }),
  promise: sonnerToast.promise,
};

/**
 * Shared toast helper for API errors.
 *
 * Preserves any `detail`/`message`/`error` string returned by the backend,
 * and falls back to the caller's domain-aware sentence — never the bare
 * word "Failed", which is unhelpful and off-brand.
 *
 * Example:
 *   try { await api.post("/materials", body); }
 *   catch (e) { showApiError(e, "Could not save the material — check your connection and try again."); }
 */
export function showApiError(err, fallbackMessage = "Something went wrong. Please try again.") {
  // 401 is handled by the global auth interceptor; don't double-toast.
  if (err?.response?.status === 401) return;

  const detail =
    err?.response?.data?.detail ||
    err?.response?.data?.message ||
    err?.response?.data?.error;

  toast.error(detail || fallbackMessage);

  if (err?.response) {
    // eslint-disable-next-line no-console
    console.warn("[api error]", err.response.status, err.response.config?.url, err.response.data);
  } else if (err) {
    // eslint-disable-next-line no-console
    console.warn("[api error]", err);
  }
}
